'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { tv } from 'tailwind-variants';
import { useSwipeable } from 'react-swipeable';
import { faChevronRight, faChevronLeft, faX } from '@fortawesome/pro-solid-svg-icons';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from './icon.component';

import { GalleryImage } from '@/app/(main)/(pages)/_components/entities/gallery-carousel/gallery-carousel.component';

interface GalleryProps {
  images: GalleryImage[];
  open: boolean;
  currentIndex: number;
  onOpenChange: (open: boolean) => void;
}

const contentModal = tv({
  slots: {
    content: [
      'data-[state=open]:animate-content-show-mobile',
      'md:data-[state=open]:animate-content-show',
      'fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]',
      'md:rounded-2xl',
      'focus:outline-none z-[51]',
      'w-full px-4 md:w-auto md:max-w-[90vw]',
    ],

    grid: 'flex items-center justify-center md:gap-4',
    imageWrapper: 'relative min-w-0',
    navButton: [
      'shrink-0 items-center justify-center',
      'w-10 h-10 md:w-12 md:h-12 rounded-full',
      'bg-black/40 hover:bg-black/60 transition-colors',
    ],
    desktopNavButton: 'hidden md:flex',
    mobileNavButton: 'absolute top-1/2 -translate-y-1/2 z-[1] flex md:hidden',
  },
});

export function Gallery({ open, images, currentIndex, onOpenChange }: GalleryProps) {
  const { content, grid, imageWrapper, navButton, desktopNavButton, mobileNavButton } = contentModal();
  const [currentIndexValue, setCurrentIndexValue] = useState(currentIndex);

  const shouldShowNavigation = images.length > 1;

  useEffect(() => {
    setCurrentIndexValue(currentIndex);
  }, [currentIndex]);

  function handleNextImage() {
    if (currentIndexValue < images.length - 1) {
      setCurrentIndexValue(currentIndexValue + 1);
    }
  }

  function handlePreviousImage() {
    if (currentIndexValue > 0) {
      setCurrentIndexValue(currentIndexValue - 1);
    }
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      handleNextImage();
    },
    onSwipedRight: () => {
      handlePreviousImage();
    },
    trackMouse: true,
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-black opacity-80 transition-opacity data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[50]">
          <Dialog.Close className="absolute top-10 right-10">
            <Icon icon={faX} className="text-white text-xl md:text-2xl" />
          </Dialog.Close>
        </Dialog.Overlay>
        <Dialog.Content className={content()}>
          <VisuallyHidden>
            <Dialog.Title>Galeria de imagens</Dialog.Title>
          </VisuallyHidden>
          <div className={grid()}>
            {shouldShowNavigation && (
              <button
                onClick={handlePreviousImage}
                aria-label="Imagem anterior"
                className={`${navButton()} ${desktopNavButton()}`}
              >
                <Icon icon={faChevronLeft} className="text-white text-2xl" />
              </button>
            )}

            <div className={imageWrapper()}>
              {images.map((image, index) => (
                <Image
                  key={image.src}
                  src={image.src}
                  width={1600}
                  height={1200}
                  sizes="(max-width: 768px) 92vw, 80vw"
                  alt="imagem expandida do parceiro"
                  data-testid={DataTestIdEnum.GALLERY_IMAGE}
                  unoptimized
                  className={
                    `rounded-lg max-h-[85vh] max-w-full w-auto h-auto object-contain ${
                      currentIndexValue === index ? 'block' : 'hidden'}`
                  }
                  {...handlers}
                />
              ))}

              {shouldShowNavigation && (
                <>
                  <button
                    onClick={handlePreviousImage}
                    aria-label="Imagem anterior"
                    className={`${navButton()} ${mobileNavButton()} left-2`}
                  >
                    <Icon icon={faChevronLeft} className="text-white text-xl" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    aria-label="Próxima imagem"
                    className={`${navButton()} ${mobileNavButton()} right-2`}
                  >
                    <Icon icon={faChevronRight} className="text-white text-xl" />
                  </button>
                </>
              )}
            </div>

            {shouldShowNavigation && (
              <button
                onClick={handleNextImage}
                aria-label="Próxima imagem"
                className={`${navButton()} ${desktopNavButton()}`}
              >
                <Icon icon={faChevronRight} className="text-white text-2xl" />
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
